import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DashboardWidget } from "@/components/boards/DashboardWidget";
import { BOARD } from "@/constants/testIds";
import api from "@/lib/api";

export default function PublicBoard() {
  const { token } = useParams();
  const [dash, setDash] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [explainingId, setExplainingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/public/dashboards/${token}`);
      setDash(data.dashboard);
      setWidgets(data.widgets);
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "This dashboard is not available.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const explain = async (w) => {
    setExplainingId(w.id);
    try {
      const { data } = await api.post(`/public/dashboards/${token}/widgets/${w.id}/explain`);
      setWidgets((prev) => prev.map((x) => (x.id === w.id ? { ...x, explanation: data.explanation } : x)));
    } catch {
      toast.error("Could not explain this chart.");
    } finally {
      setExplainingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 text-center text-sm text-muted-foreground">
        {err}
      </div>
    );
  }

  return (
    <div data-testid={BOARD.publicRoot} className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
            <span className="text-sm font-semibold">sema</span>
          </a>
          <a href={`${api.defaults.baseURL}/public/dashboards/${token}/pdf`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="rounded-full text-xs">
              <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
            </Button>
          </a>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl px-6 py-8">
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{dash.name}</h1>
        <p className="mt-1 text-xs text-muted-foreground">{widgets.length} visualization(s) · shared via Sema</p>

        {widgets.length ? (
          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {widgets.map((w) => (
              <DashboardWidget key={w.id} widget={w} onExplain={explain} explaining={explainingId === w.id} />
            ))}
          </div>
        ) : (
          <p className="mt-10 text-sm text-muted-foreground">This dashboard has no visualizations yet.</p>
        )}

        <footer className="mt-12 border-t border-border/60 pt-6 text-center text-xs text-muted-foreground">
          Built with <a href="/" className="text-accent">Sema</a> — the semantic layer for your data.
        </footer>
      </main>
    </div>
  );
}
